/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react'
import { Button, Col, Container, Form, Modal, Row } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { setYears, showSynopsis } from '../../../redux/actions/synopsis'
import { formatMiddleName, buildPatientData, isObjectEmpty, formatPatientData, formatPatientMedications } from '../../../utils/helpers'
import { Table, Column, HeaderCell, Cell } from 'rsuite-table'
import { compareDesc, format, isBefore, isWithinInterval, subYears } from 'date-fns'
import { PlusSquare, InfoCircle } from 'react-bootstrap-icons'
import Chart from 'react-apexcharts'
import ChartJS from 'chart.js'

import 'rsuite-table/dist/css/rsuite-table.css'

const PatientSynopsisModal = () => {
  const [selected, setSelected] = useState(['0-1'])
  const [columns, setColumns] = useState([])
  const [patientData, setPatientData] = useState([])
  const [patientMedData, setPatientMedData] = useState([])
  const [dataSet, setDataSet] = useState(false)

  const dispatch = useDispatch()
  const { 
    show, 
    patient, 
    years, 
    s2kScores, 
    encounter, 
    s2kItems, 
    s2k, 
    labs, 
    procedures, 
    medications, 
    pga, 
    pgas, 
    damage, 
    damages
  } = useSelector((state) => ({
    show: state.synopsis.show,
    patient: state.patient.patient,
    years: state.synopsis.years,
    s2kScores: state.assessment.s2kScores,
    s2kItems: state.assessment.s2kItems,
    s2k: state.assessment.s2k,
    encounter: state.patient.encounter,
    labs: state.patient.observations,
    procedures: state.patient.procedures,
    medications: state.patient.medications,
    pga: state.assessment.pga,
    pgas: state.assessment.pgas,
    damage: state.assessment.damage,
    damages: state.assessment.damages
  }))

  useEffect(() => {
    if (show) {
      setPatientData(buildPatientData(columns, labs, medications, procedures, s2kItems, s2k, s2kScores, encounter.date, pgas, pga, damage, damages, years))
      setPatientMedData(formatPatientMedications(medications, encounter.date, years))
      setDataSet(true)
    }
  }, [show])

  useEffect(() => {
    if (dataSet) {
      const s2kDates = s2kScores.map((s2kScore) => format(new Date(s2kScore.encounterDate), 'MM/dd/yyyy'))
      const labDates = labs.reduce((acc, curr) => {
        if (isBefore(new Date(curr.effectiveDate), new Date(encounter.date))) {
          return [...acc, format(new Date(curr.effectiveDate), 'MM/dd/yyyy')]
        }
        return acc
      }, [])
      const medDates =  medications.reduce((acc, curr) => {
        return [...acc, ...curr.medications.map((med) => format(new Date(med.startDate), 'MM/dd/yyyy'))]
      }, [])
      const procedureDates = procedures.reduce((acc, curr) => {
        if (isBefore(new Date(curr.performedDate), new Date(encounter.date))) {
          return [...acc, format(new Date(curr.performedDate), 'MM/dd/yyyy')]
        }
        return acc
      }, [])

      const tableColumns = [
        ...s2kDates,
        ...labDates,
        ...medDates,
        ...procedureDates,
        format(new Date(encounter.date), 'MM/dd/yyyy')
      ]
        .filter((date) => {
          const compareEndDate = new Date(encounter.date)
          const compareStartDate = subYears(compareEndDate, years)
          return isWithinInterval(new Date(date), { start: compareStartDate, end: compareEndDate })
        })
        .sort((a, b) => compareDesc(new Date(b), new Date(a)))
        .reduce((a, b) => {
          if (a.slice(-1)[0] !== b) {
            a.push(b)
          }
          return a
        }, [])

      setColumns(tableColumns)
      setSelected(['0-1'])
    }
  }, [dataSet, years])

  useEffect(() => {
    if (dataSet && selected) {
      const ctx = document.getElementById('synopsis-chart')
      const { labels, datasets } = formatPatientData(selected, patientData, columns)

      
      if (datasets.length > 0) {
        new ChartJS(ctx, {
          type: 'line',
          data: {
            labels,
            datasets
          },
          options: {
            maintainAspectRatio: false,
            layout: {
              padding: 15
            },
            scales: {
              xAxes: [{
                ticks: {
                  display: false
                }
              }],
              yAxes: [{
                ticks: {
                  display: false
                }
              }]
            },
            legend: {
              display: false
            }
          }
        })
      }
    }
  }, [dataSet, selected])

  const handleSelectRow = (rowId) => {
    setSelected((ps) => {
      if (ps.includes(rowId)) {
        return ps.filter((s) => s !== rowId)
      }
      return [...ps, rowId]
    })
  }

  const colorDecider = (rowId) => {
    const index = selected.indexOf(rowId)
    switch (index) {
    case 0:
      return 'slate-gray font-weight-bold'
    case 1:
      return 'purple font-weight-bold'
    case 2:
      return 'green font-weight-bold'
    default:
      return 'slate-gray'
    }
  }

  const options = {
    chart: {
      type: 'rangeBar',
      height: 300,
      toolbar: {
        show: false
      },
      zoom: {
        enabled: false
      }
    },
    colors: ['#c6e0eb'],
    dataLabels: {
      enabled: false
    },
    legend: {
      show: false
    },
    plotOptions: {
      bar: {
        horizontal: true
      }
    },
    states: {
      hover: {
        filter: {
          type: 'none',
          value: 0
        }
      },
      active: {
        filter: {
          type: 'none',
          value: 0
        }
      }
    },
    tooltip: {
      x: {
        format: 'MM/dd/yyyy'
      },
      y: {
        formatter: () => ''
      }
    },
    xaxis: {
      type: 'datetime',
      position: 'top',
      tickAmount: columns.length,
      labels: {
        datetimeUTC: true,
        datetimeFormatter: {
          year: 'MM/yyyy',
          month: 'MM/yyyy',
          day: 'MM/yyyy',
        },
      }
    },
    yaxis: {
      labels: {
        align: 'left',
        style: {
          // fontSize: '.75em',
          fontFamily: '\'Zilla Slab\', serif'
        }
      },
      axisBorder: {
        color: '#78909C',
        offsetX: 0,
        offsetY: 0
      }
    }
  }

  return (
    <Modal
      id='synopsis-modal'
      dialogClassName='synopsis-modal'
      size='lg'
      show={show}
      onHide={() => {
        dispatch(showSynopsis(!show))
        setDataSet(false)
      }}
      backdrop='static'
      keyboard={false}
    >
      <Modal.Header className='justify-content-center'>
        <Modal.Title className='h6'>
          Patient Synopsis
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='p-0'>
        <Container fluid>
          <Row id='synopsis-patient'>
            <Col xs={9}>
              <Row>
                <Col xs='auto'>
                  <h3 className='h5'>Patient Synopsis</h3>
                </Col>
                <Col xs='auto' className='font-lg'>
                  {patient && `${patient.firstName} ${formatMiddleName(patient.middleName)} ${patient.lastName}`}
                </Col>
                <Col xs='auto' className='font-lg'>
                  {patient && `MRN: ${patient.mrn}`}
                </Col>
              </Row>
            </Col>
            <Col xs={3}>
              <Form.Control
                id='year-select'
                as='select'
                size='sm'
                value={years}
                onChange={(event) => dispatch(setYears(event.target.value))}
              >
                <option value={1}>1 Year</option>
                <option value={2}>2 Years</option>
                <option value={3}>3 Years</option>
                <option value={4}>4 Years</option>
              </Form.Control>
            </Col>
          </Row>
          <Row id='synopsis-patient-body' className='px-2'>
            <Col md={6} sm={12} id='synopsis-patient-info' className='px-0'>
              <Table
                isTree
                defaultExpandAllRows={dataSet}
                rowKey='id'
                className='synopsis-table'
                data={dataSet ? patientData : []}
                shouldUpdateScroll={false}
                height={665}
                renderTreeToggle={(icon, rowData) => <span className='font-weight-bold font-md'>{rowData.title} <InfoCircle className='mx-1'/> {icon}</span>}
                rowHeight={(rowData) => {
                  if (rowData) {
                    if (!isObjectEmpty(rowData) && rowData.title.length > 35) {
                      return 46
                    } else if (!isObjectEmpty(rowData) && rowData.title.length > 60) {
                      return 58
                    } else if (!isObjectEmpty(rowData) && rowData.title.length > 80) {
                      return 65
                    } else if (!isObjectEmpty(rowData) && rowData.title.length > 100) {
                      return 100
                    }
                  }
                  return 36
                }}
              >
                <Column treeCol width={255} fixed>
                  <HeaderCell></HeaderCell>
                  <Cell dataKey='title'>
                    {
                      (rowData) => {
                        if (rowData && typeof rowData.id === 'string') {
                          const parentId = parseInt(rowData.id.split('-')[0], 10)
                          if (parentId === 4) {
                            return <span className='slate-gray'>{rowData.title}</span>
                          } else {
                            return (
                              <div className='custom-control custom-checkbox'>
                                <input
                                  type='checkbox'
                                  className='custom-control-input'
                                  checked={selected.includes(rowData.id)}
                                  onChange={() => handleSelectRow(rowData.id)}
                                  disabled={selected.length === 3 && !selected.includes(rowData.id)}
                                  name={rowData.id}
                                  id={rowData.id}
                                />
                                <label className={`custom-control-label text-wrap ${colorDecider(rowData.id)}`} htmlFor={rowData.id}>{rowData.title}</label>
                              </div>
                            )
                          }
                        }
                        return null
                      }
                    }
                  </Cell>
                </Column>
                {
                  columns.map((column) => (
                    <Column key={column}>
                      <HeaderCell>{column}</HeaderCell>
                      <Cell dataKey={column}>
                        {
                          (rowData) => {
                            if (rowData && rowData[column] === true) {
                              return <PlusSquare />
                            }

                            return <span className={colorDecider(rowData.id)}>{rowData[column]}</span>
                          }
                        }
                      </Cell>
                    </Column>
                  ))
                }
              </Table>
            </Col>
            <Col md={6} sm={12}>
              <div style={{ maxHeight: 300 }} className='mb-5'>
                <Row>
                  <Col className='slate-gray'>
                    Trend Graph
                  </Col>
                  <Col className='slate-gray text-right'>
                    <Button variant='light' size='sm' className='font-85' onClick={() => setSelected([])}>
                      Clear Graph
                    </Button>
                  </Col>
                </Row>
                <Row>
                  {
                    selected.map((id) => {
                      const data = [].concat(...patientData.map((pd) => pd.children))
                      const dataPoint = data.find((d) => id === d.id)
                      return (
                        <Col key={id} className={`text-truncate ${colorDecider(id)} font-85`} xs={3}>
                          {dataPoint ? dataPoint.title : ''}
                        </Col>
                      )
                    })
                  }
                </Row>
                {
                  selected.length > 0 ? (
                    <div id='synopsis-charts' style={{ height: 300, position: 'relative' }}>
                      <canvas id='synopsis-chart'></canvas>
                    </div>
                  ) : (
                    <div className='text-center slate-gray'>
                      {/* PROBABLY NEED TO CHANGE LOL */}
                      Please select what to graph in table on left.
                    </div>
                  )
                }
                
              </div>
              <div className='mt-1 slate-gray'>
                Medication History
                {
                  patientMedData.length > 0 && patientMedData[0].data.length > 0 ? (
                    <div id='synopsis-charts'>
                      <Chart
                        options={options}
                        series={patientMedData}
                        type='rangeBar'
                        height={285}
                      />
                    </div>
                  ) : (
                    <div className='text-center'>
                      {/* PROBABLY NEED TO CHANGE LOL */}
                          Patient is not on any Lupus Medications
                    </div>
                  )
                }
              </div>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' size='sm' onClick={() => dispatch(showSynopsis(!show))}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default PatientSynopsisModal